import { Vehicle } from '@otw/models/inventory/inventory';
import { POST_SALE_EMAIL_CONTACTS } from '../constants/shippingIntroductionContacts';

export const POST_SALE_EMAIL_SUBJECT =
  'Your Optic Truck Works Purchase – Next Steps & Delivery Information';

export type PostSalePurchaseType = 'cash' | 'financed';

export type PostSaleMvSection =
  | 'translease_lease'
  | 'nj_registration'
  | 'out_of_state_cash'
  | 'out_of_state_financed';

export interface PostSaleEmailSalesRep {
  name: string;
  phone: string;
  email: string;
}

export interface PostSaleEmailSecondaryContact {
  name: string;
  phone: string;
}

export interface PostSaleEmailTextInput {
  /** Customer first name for the greeting. */
  customerFirstName: string;
  /** Customer or company name for the contact-on-file block. */
  customerOrCompanyName: string;
  contactPhone: string;
  contactEmail: string;
  secondaryContact?: PostSaleEmailSecondaryContact | null;
  salesRep: PostSaleEmailSalesRep;
  vehicles: Vehicle[];
  getDeliveryAddressBlock: (vehicleId: string) => string;
  /**
   * Registration state for MV section selection (e.g. dropoff or business address).
   * Compared case-insensitively as NJ / New Jersey.
   */
  registrationState: string;
  /** When true, TransLease Lease MV section wins regardless of registration state. */
  transleaseLease: boolean;
  purchaseType: PostSalePurchaseType;
}

export const isNewJerseyRegistrationState = (
  state: string | null | undefined,
): boolean => {
  const normalized = state?.trim().toLowerCase().replace(/\./g, '') ?? '';
  return normalized === 'nj' || normalized === 'new jersey';
};

export const resolvePostSalePurchaseType = (
  vehicle: Vehicle | null | undefined,
): PostSalePurchaseType => {
  const paymentType = vehicle?.finance?.payment_type;
  if (paymentType === 'finance' || paymentType === 'customer_bank') {
    return 'financed';
  }
  return 'cash';
};

/**
 * Exactly one MV section. Order: TransLease → NJ → out-of-state cash → out-of-state financed.
 */
export const selectPostSaleMvSection = (input: {
  transleaseLease: boolean;
  registrationState: string;
  purchaseType: PostSalePurchaseType;
}): PostSaleMvSection => {
  if (input.transleaseLease) return 'translease_lease';
  if (isNewJerseyRegistrationState(input.registrationState)) {
    return 'nj_registration';
  }
  if (input.purchaseType === 'cash') return 'out_of_state_cash';
  return 'out_of_state_financed';
};

const MV_SECTION_BODY: Record<PostSaleMvSection, string[]> = {
  translease_lease: [
    'Because your transaction is a lease, TransLease will provide your temporary tag and coordinate the applicable title and registration process.',
    '',
    'Please follow any instructions you receive from TransLease regarding your temporary tag and registration.',
  ],
  nj_registration: [
    'Optic will handle the New Jersey title and registration process.',
    '',
    'To complete the process, we will need:',
    '',
    'Your New Jersey Corp Code',
    '',
    'Confirmation of the mailing address where you would like your physical temporary tag sent',
    '',
    "A photo of your driver's license",
    '',
    'Proof of insurance for the vehicle',
    '',
    "You do not need to provide the driver's license or proof of insurance immediately. Please have these available before your vehicle is ready for delivery so we can process your temporary tag without delay.",
  ],
  out_of_state_cash: [
    'Optic will provide your temporary tag by email, and mail the vehicle title directly to you. You will be responsible for completing the permanent registration process in your state before your temporary registration expires.',
    '',
    'Before Optic can issue your temporary tag, we will need:',
    '',
    'Confirmation of the mailing address where you would like your title sent',
    '',
    "A photo of your driver's license",
    '',
    'Proof of insurance for the vehicle',
    '',
    "You do not need to provide the driver's license or proof of insurance immediately. Please have these available before your vehicle is ready for delivery so we can process your temporary tag and physical title without delay.",
  ],
  out_of_state_financed: [
    'Optic will provide your temporary tag by email.',
    '',
    "Because your vehicle was financed, your lender determines how your title and registration packet is processed. Depending on the lender's requirements, the packet may be sent directly to you or directly to the appropriate motor vehicle agency.",
    '',
    'Please follow any registration instructions you receive promptly so your permanent registration can be completed before your temporary registration expires.',
    '',
    'Before Optic can issue your temporary tag, we will need:',
    '',
    "A photo of your driver's license",
    '',
    'Proof of insurance for the vehicle',
    '',
    "You do not need to provide the driver's license or proof of insurance immediately. Please have these available before your vehicle is ready for delivery so we can process your temporary tag without delay.",
  ],
};

const formatVehicleSummaryLine = (vehicle: Vehicle): string => {
  const yearMakeModel = [vehicle.year, vehicle.make, vehicle.model]
    .map((part) => (part != null ? String(part).trim() : ''))
    .filter(Boolean)
    .join(' ');
  return yearMakeModel || 'N/A';
};

const formatVehicleBlock = (vehicle: Vehicle): string[] => {
  const vinLine = vehicle.vin?.trim()
    ? `VIN: ${vehicle.vin.trim()}`
    : 'VIN: N/A';
  return [formatVehicleSummaryLine(vehicle), vinLine];
};

const formatContactLines = (
  name: string,
  phone: string,
  email: string,
): string[] => {
  const lines = [name.trim() || 'N/A'];
  if (phone.trim()) lines.push(phone.trim());
  if (email.trim()) lines.push(email.trim());
  return lines;
};

const formatDeliveryBlock = (
  vehicle: Vehicle,
  getDeliveryAddressBlock: (vehicleId: string) => string,
): string[] => {
  const address = getDeliveryAddressBlock(vehicle.id);
  return ['Delivery Address:', address];
};

export const generatePostSaleEmailText = (
  input: PostSaleEmailTextInput,
): string => {
  const {
    customerFirstName,
    customerOrCompanyName,
    contactPhone,
    contactEmail,
    secondaryContact,
    salesRep,
    vehicles,
    getDeliveryAddressBlock,
    registrationState,
    transleaseLease,
    purchaseType,
  } = input;

  const { shippingDelivery, repairsScheduleB, generalManager } =
    POST_SALE_EMAIL_CONTACTS;

  const greetingName = customerFirstName.trim() || 'there';
  const mvSection = selectPostSaleMvSection({
    transleaseLease,
    registrationState,
    purchaseType,
  });

  const vehicleBlocks = vehicles.flatMap((vehicle, index) => {
    const block = formatVehicleBlock(vehicle);
    return index < vehicles.length - 1 ? [...block, ''] : block;
  });

  const deliveryBlocks = vehicles.flatMap((vehicle, index) => {
    const block = formatDeliveryBlock(vehicle, getDeliveryAddressBlock);
    return index < vehicles.length - 1 ? [...block, ''] : block;
  });

  const showSecondary =
    Boolean(secondaryContact?.name?.trim()) ||
    Boolean(secondaryContact?.phone?.trim());

  const lines: string[] = [
    `Subject: ${POST_SALE_EMAIL_SUBJECT}`,
    '',
    `Hello ${greetingName},`,
    '',
    'Thank you for your purchase! Our team is preparing your truck for delivery.',
    '',
    'Please keep this email for reference throughout the post-sale process. It contains your vehicle and delivery information, important title and registration details, and the contacts you may need along the way.',
    '',
    'YOUR OPTIC CONTACTS',
    'Shipping & Delivery',
    ...formatContactLines(
      shippingDelivery.name,
      shippingDelivery.phone,
      shippingDelivery.email,
    ),
    '',
    'Repairs / Schedule B',
    ...formatContactLines(
      repairsScheduleB.name,
      repairsScheduleB.phone,
      repairsScheduleB.email,
    ),
    '',
    'Sales',
    ...formatContactLines(salesRep.name, salesRep.phone, salesRep.email),
    '',
    'General Manager',
    ...formatContactLines(
      generalManager.name,
      generalManager.phone,
      generalManager.email,
    ),
    '',
    'PURCHASED VEHICLE(S)',
    ...vehicleBlocks,
    '',
    'DELIVERY INFORMATION',
    ...deliveryBlocks,
    '',
    'SCHEDULE B & REPAIRS',
    'Your Schedule B Guarantee is attached to this email. Please keep it for reference throughout the post-sale process.',
    '',
    'If you have questions regarding repairs or items documented on your Schedule B, please contact Cody Rodriguez.',
    '',
    'TITLE, REGISTRATION & TEMPORARY TAG',
    ...MV_SECTION_BODY[mvSection],
    '',
    'IMPORTANT REGISTRATION REMINDER',
    'Your temporary tag gives you a limited window to complete the permanent registration process. Please complete any paperwork or other requirements promptly and before your temporary registration expires.',
    '',
    'If your vehicle was financed, remember that your lender determines whether your title and registration packet is sent to you or directly to the appropriate motor vehicle agency. Reach out to your Optic Sales representative if you need contact information for your lender.',
    '',
    'YOUR CONTACT INFORMATION ON FILE (PLEASE NOTIFY US IF INCORRECT)',
    'Customer:',
    customerOrCompanyName.trim() || 'N/A',
    '',
    'Phone:',
    contactPhone.trim() || 'N/A',
    '',
    'Email:',
    contactEmail.trim() || 'N/A',
  ];

  if (showSecondary) {
    lines.push(
      '',
      'Secondary contact:',
      secondaryContact?.name?.trim() || 'N/A',
      secondaryContact?.phone?.trim() || 'N/A',
    );
  }

  lines.push(
    '',
    'Thank you again for your business. We appreciate the opportunity to work with you and look forward to getting your truck delivered.',
    '',
    'Best Regards,',
    'Optic Truck Works',
    '',
  );

  return lines.join('\n');
};
